// src/modules/settings/TenantSettingsEditForm.tsx
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ChevronDown, ChevronUp, Edit2, Save, X, AlertCircle, Loader2, Info, TrendingUp, TrendingDown, Monitor } from 'lucide-react';
import {
  DefatomAgentSettings,
  RemediationSettings,
  ComplianceSettings,
  PatchBySeverityConfiguration,
  ReportExpiryMinutes,
  ReportsSettings,
  FeatureListEntry,
} from '../../components/services/TenantSettingsService';
import { getSeverityIcon } from '../../common/Utils/severityHelpers';
import { formatMinutesReference, parseRange } from './helpers/rangeValidation';
import {
  DEFAULT_DEFATOM_AGENT_SETTINGS,
  DEFAULT_REMEDIATION_SETTINGS,
  DEFAULT_COMPLIANCE_SETTINGS,
  DEFAULT_CVE_MAPPING,
  DEFAULT_DAYS_RANGE,
  DEFAULT_SPLASHTOP_SETTINGS,
} from './constants/settingsDefaults';
import { TenantGroupOption, toRemoteAccessForm } from './helpers/remoteAccessSettings';
import { RemoteAccessEditFields } from './RemoteAccessSettings';
import CollapsibleSection from '../../components/CollapsibleSection';
import { getSeverityColor } from '../../common/Utils/color';

interface TenantSettingsEditFormProps {
  tenantData: {
    id: string;
    tenantId: string;
    tenantName: string;
    settings: {
      defatomAgent: DefatomAgentSettings;
      remediation: RemediationSettings;
      complaince: ComplianceSettings;
      patchBySeverityConfiguration: PatchBySeverityConfiguration;
      reports: ReportsSettings;
      featureList: FeatureListEntry[] | null;
    };
  };
  onSubmit: (values: any, formikHelpers: any) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  /** null when the tenant's groups couldn't be loaded. */
  tenantGroups: TenantGroupOption[] | null;
  expandedSections: Record<string, boolean>;
  toggleSection: (section: string) => void;
}

// Validation Schema
const validationSchema = Yup.object().shape({
  // Defatom Agent Settings - all required and numeric
  numberOfDaysForIdentifyingDisconncetedEndpoint: Yup.number()
    .required('This field is required')
    .positive('Must be a positive number')
    .integer('Must be a whole number')
    .min(1, 'Must be at least 1')
    .max(365, 'Must be at most 365'),

  maxEndpointDisconnectedThresholdInDays: Yup.number()
    .required('This field is required')
    .positive('Must be a positive number')
    .integer('Must be a whole number')
    .min(1, 'Must be at least 1')
    .max(365, 'Must be at most 365'),

  thresholdInMinutesForTaggingAgentAsOffline: Yup.number()
    .required('This field is required')
    .positive('Must be a positive number')
    .integer('Must be a whole number')
    .min(1, 'Must be at least 1')
    .max(1440, 'Must be at most 1440'),

  thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan: Yup.number()
    .required('This field is required')
    .positive('Must be a positive number')
    .integer('Must be a whole number')
    .min(1, 'Must be at least 1')
    .max(1440, 'Must be at most 1440'),

  // Remediation Settings - required and numeric
  numberOfSoftwareVersionsAvailableForRemediation: Yup.number()
    .required('This field is required')
    .positive('Must be a positive number')
    .integer('Must be a whole number')
    .min(1, 'Must be at least 1')
    .max(10, 'Must be at most 10'),

  // Compliance Settings - required and numeric
  currentPreviousDurationInDays: Yup.number()
    .required('This field is required')
    .positive('Must be a positive number')
    .integer('Must be a whole number')
    .min(1, 'Must be at least 1')
    .max(365, 'Must be at most 365'),

  nonComplainceDefinitionDays: Yup.number()
    .required('This field is required')
    .positive('Must be a positive number')
    .integer('Must be a whole number')
    .min(1, 'Must be at least 1')
    .max(365, 'Must be at most 365'),

  // Patch Severity Configuration - CVE Score Mapping (Critical > High > Medium > Low)
  severityCVEMapping: Yup.array()
    .of(
      Yup.object().shape({
        severity: Yup.string().required('Severity is required'),
        cveScore: Yup.string()
          .required('CVE Score is required')
          .matches(/^[><]=?\d+(\.\d+)?$|^\d+(\.\d+)?-\d+(\.\d+)?$/, 'Must be in format: >8, >=4, <3, or 0-3'),
      }),
    )
    .test('cve-order', 'CVE scores must be in descending order (Critical highest, Low lowest)', function (value) {
      if (!value || value.length !== 4) return true;

      const ranges = value.map((v) => parseRange(v.cveScore));
      if (ranges.some((r) => r === null)) return true;

      // Check that ranges are in descending order and cover all values
      for (let i = 0; i < ranges.length - 1; i++) {
        const current = ranges[i];
        const next = ranges[i + 1];

        if (!current || !next) continue;

        // Critical should be highest (e.g., >8 or >=8)
        // Low should be lowest (e.g., <1 or 0-1)
        if (current.type === 'gt' || current.type === 'gte') {
          if (next.type === 'gt' || next.type === 'gte') {
            if (current.value <= next.value) {
              return this.createError({
                path: `severityCVEMapping[${i + 1}].cveScore`,
                message: 'Each range must be lower than the previous',
              });
            }
          }
        }
      }

      return true;
    }),

  // Days Range Configuration (Range 1 highest > Range 4 lowest)
  daysRange: Yup.array()
    .of(
      Yup.string()
        .required('Days range is required')
        .matches(/^[><]=?\d+$|^\d+-\d+$/, 'Must be in format: >90, >=60, <30, or 61-90'),
    )
    .test('days-order', 'Day ranges must be in descending order without gaps', function (value) {
      if (!value || value.length !== 4) return true;

      const ranges = value.map((v) => parseRange(v));
      if (ranges.some((r) => r === null)) return true;

      // Validate ordering: Range 1 should be highest, Range 4 should be lowest
      for (let i = 0; i < ranges.length - 1; i++) {
        const current = ranges[i];
        const next = ranges[i + 1];

        if (!current || !next) continue;

        // Example correct order: >90, 61-90, 31-60, <30
        // Range 1: >90 (highest)
        // Range 2: 61-90
        // Range 3: 31-60
        // Range 4: <30 (lowest)

        if (current.type === 'gt' || current.type === 'gte') {
          // First range should be "greater than" (e.g., >90)
          if (i !== 0) {
            return this.createError({
              path: `daysRange[${i}]`,
              message: 'Only first range can use > or >=',
            });
          }

          // Next should be a range or less than
          if (next.type === 'range') {
            // Check continuity: >90 followed by X-90 where X < 90
            if (next.value2 && next.value2 >= current.value) {
              return this.createError({
                path: `daysRange[${i + 1}]`,
                message: 'Range must be lower than previous threshold',
              });
            }
          }
        } else if (current.type === 'range') {
          // Middle ranges
          if (next.type === 'range') {
            // Check continuity: 61-90 followed by X-60 where X < 61
            if (next.value2 && next.value2 >= current.value) {
              return this.createError({
                path: `daysRange[${i + 1}]`,
                message: 'Ranges must be continuous without gaps',
              });
            }
          } else if (next.type === 'lt' || next.type === 'lte') {
            // Last range should be "less than" (e.g., <30)
            if (next.value >= current.value) {
              return this.createError({
                path: `daysRange[${i + 1}]`,
                message: 'Final range must be lower than previous',
              });
            }
          }
        } else if (current.type === 'lt' || current.type === 'lte') {
          // "Less than" should only be the last range
          if (i !== ranges.length - 1) {
            return this.createError({
              path: `daysRange[${i}]`,
              message: 'Only last range can use < or <=',
            });
          }
        }
      }

      return true;
    }),

  reportExpiryMinutes: Yup.object().test('valid-report-expiry', 'Report expiry values must be positive whole numbers', function (value) {
    if (!value) return true;
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      const n = Number(val);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
        return this.createError({
          path: `reportExpiryMinutes.${key}`,
          message: 'Must be a positive whole number (minutes)',
        });
      }
    }
    return true;
  }),
  reportListingThresholdInDays: Yup.number()
    .required('This field is required')
    .positive('Must be a positive number')
    .integer('Must be a whole number')
    .min(1, 'Must be at least 1'),
  useVendorPackageRepoAsPrimary: Yup.boolean(),
  remoteAccess: Yup.object({
    idleTimeoutMinutes: Yup.number()
      .typeError('Must be a number')
      .required('This field is required')
      .integer('Must be a whole number')
      .min(0, 'Must be 0 or more')
      .max(1440, 'Must be at most 1440'),
    groupIds: Yup.array().when('limitToGroups', {
      is: true,
      then: (schema) => schema.min(1, 'Pick at least one group, or turn off "Only some groups"'),
    }),
  }),
});

const EditInputField = ({ label, name, type = 'number', helpText }: { label: string; name: string; type?: string; helpText?: string }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
    <Field
      type={type}
      name={name}
      className="w-full bg-[#0f0f0f] border border-gray-600 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all hover:border-gray-500"
      style={{ backgroundColor: '#0f0f0f' }}
    />
    <ErrorMessage name={name}>
      {(msg) => (
        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
          <AlertCircle size={12} />
          {msg}
        </p>
      )}
    </ErrorMessage>
    {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
  </div>
);

const TenantSettingsEditForm: React.FC<TenantSettingsEditFormProps> = ({
  tenantData,
  onSubmit,
  onCancel,
  saving,
  tenantGroups,
  expandedSections,
  toggleSection,
}) => {
  const initialValues = {
    numberOfDaysForIdentifyingDisconncetedEndpoint:
      tenantData.settings.defatomAgent?.numberOfDaysForIdentifyingDisconncetedEndpoint ??
      DEFAULT_DEFATOM_AGENT_SETTINGS.numberOfDaysForIdentifyingDisconncetedEndpoint,
    maxEndpointDisconnectedThresholdInDays:
      tenantData.settings.defatomAgent?.maxEndpointDisconnectedThresholdInDays ??
      DEFAULT_DEFATOM_AGENT_SETTINGS.maxEndpointDisconnectedThresholdInDays,
    thresholdInMinutesForTaggingAgentAsOffline:
      tenantData.settings.defatomAgent?.thresholdInMinutesForTaggingAgentAsOffline ??
      DEFAULT_DEFATOM_AGENT_SETTINGS.thresholdInMinutesForTaggingAgentAsOffline,
    thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan:
      tenantData.settings.defatomAgent?.thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan ??
      DEFAULT_DEFATOM_AGENT_SETTINGS.thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan,
    useVendorPackageRepoAsPrimary:
      tenantData.settings.defatomAgent?.useVendorPackageRepoAsPrimary ?? DEFAULT_DEFATOM_AGENT_SETTINGS.useVendorPackageRepoAsPrimary,
    numberOfSoftwareVersionsAvailableForRemediation:
      tenantData.settings.remediation?.numberOfSoftwareVersionsAvailableForRemediation ??
      DEFAULT_REMEDIATION_SETTINGS.numberOfSoftwareVersionsAvailableForRemediation,
    currentPreviousDurationInDays:
      tenantData.settings.complaince?.currentPreviousDurationInDays ?? DEFAULT_COMPLIANCE_SETTINGS.currentPreviousDurationInDays,
    nonComplainceDefinitionDays:
      tenantData.settings.complaince?.nonComplainceDefinitionDays ?? DEFAULT_COMPLIANCE_SETTINGS.nonComplainceDefinitionDays,
    severityCVEMapping:
      Array.isArray(tenantData.settings.patchBySeverityConfiguration?.severityCVEMapping) &&
      tenantData.settings.patchBySeverityConfiguration.severityCVEMapping.length === 4
        ? tenantData.settings.patchBySeverityConfiguration.severityCVEMapping
        : DEFAULT_CVE_MAPPING,
    daysRange:
      Array.isArray(tenantData.settings.patchBySeverityConfiguration?.daysRange) &&
      tenantData.settings.patchBySeverityConfiguration.daysRange.length === 4
        ? tenantData.settings.patchBySeverityConfiguration.daysRange
        : DEFAULT_DAYS_RANGE,
    reportExpiryMinutes: tenantData.settings.reports?.ReportExpiryMinutes || {},
    reportListingThresholdInDays: tenantData.settings.reports?.ReportListingThresholdInDays ?? 30,
    remoteAccess: toRemoteAccessForm(tenantData.settings.featureList, DEFAULT_SPLASHTOP_SETTINGS),
  };
  const remoteAccessAvailable = tenantData.settings.featureList !== null;

  return (
    <div className="p-6 text-white">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Edit2 size={24} className="text-red-500" />
            Edit Tenant Settings
          </h2>
          <p className="text-gray-400 text-sm mt-1">Modify your tenant configuration settings below</p>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ values, isSubmitting, errors, touched }) => (
          <Form className="space-y-6">
            {/* Defatom Agent Settings */}
            <CollapsibleSection
              title="Defatom Agent Settings"
              sectionKey="defatomAgent"
              icon={<AlertCircle size={20} />}
              isExpanded={expandedSections.defatomAgent}
              onToggle={() => toggleSection('defatomAgent')}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <EditInputField
                  label="Days for Identifying Disconnected Endpoint"
                  name="numberOfDaysForIdentifyingDisconncetedEndpoint"
                  helpText="Number of days before marking an endpoint as disconnected (1-365)"
                />
                <EditInputField
                  label="Max Endpoint Disconnected Threshold (days)"
                  name="maxEndpointDisconnectedThresholdInDays"
                  helpText="Maximum threshold before taking action (1-365)"
                />
                <EditInputField
                  label="Agent Offline Threshold (minutes)"
                  name="thresholdInMinutesForTaggingAgentAsOffline"
                  helpText="Minutes before tagging agent as offline (1-1440)"
                />
                <EditInputField
                  label="Remediation Plan Offline Threshold (minutes)"
                  name="thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan"
                  helpText="Offline threshold for remediation plans (1-1440)"
                />
              </div>
            </CollapsibleSection>
            {/* Use Vendor Package Repository */}
            <div className="mb-4 col-span-full">
              <label className="flex items-center justify-between p-4 bg-[#0f0f0f] border border-gray-600 rounded-lg hover:border-gray-500 transition-all cursor-pointer group">
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                    Use Vendor Package Repository as Primary
                  </span>
                  <span className="text-xs text-gray-500 mt-1">Enable to use vendor repository as the primary package source</span>
                </div>
                <Field name="useVendorPackageRepoAsPrimary">
                  {({ field, form }: any) => (
                    <div
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        field.value ? 'bg-red-600' : 'bg-gray-600'
                      }`}
                      onClick={() => form.setFieldValue('useVendorPackageRepoAsPrimary', !field.value)}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          field.value ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </div>
                  )}
                </Field>
              </label>
              <ErrorMessage name="useVendorPackageRepoAsPrimary">
                {(msg) => (
                  <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {msg}
                  </p>
                )}
              </ErrorMessage>
            </div>
            {/* Remote Access (Splashtop) */}
            <CollapsibleSection
              title="Remote Access"
              sectionKey="remoteAccess"
              icon={<Monitor size={20} />}
              isExpanded={expandedSections.remoteAccess}
              onToggle={() => toggleSection('remoteAccess')}
            >
              {remoteAccessAvailable ? (
                <RemoteAccessEditFields tenantGroups={tenantGroups} />
              ) : (
                <p className="text-sm text-gray-400 flex items-center gap-2 p-4">
                  <AlertCircle size={16} className="text-red-400" />
                  The remote access settings couldn&apos;t be loaded, so they can&apos;t be changed right now. Reload the page to try again.
                </p>
              )}
            </CollapsibleSection>

            {/* Remediation Settings */}
            <CollapsibleSection
              title="Remediation Settings"
              sectionKey="remediation"
              icon={<Save size={20} />}
              isExpanded={expandedSections.remediation}
              onToggle={() => toggleSection('remediation')}
            >
              <EditInputField
                label="Software Versions Available for Remediation"
                name="numberOfSoftwareVersionsAvailableForRemediation"
                helpText="Number of previous software versions to keep for rollback (1-10)"
              />
            </CollapsibleSection>

            {/* Compliance Settings */}
            <CollapsibleSection
              title="Compliance Settings"
              sectionKey="compliance"
              icon={<AlertCircle size={20} />}
              isExpanded={expandedSections.compliance}
              onToggle={() => toggleSection('compliance')}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <EditInputField
                  label="Current Previous Duration (days)"
                  name="currentPreviousDurationInDays"
                  helpText="Duration for current vs previous comparison (1-365)"
                />
                <EditInputField
                  label="Non-Compliance Definition (days)"
                  name="nonComplainceDefinitionDays"
                  helpText="Days before marking as non-compliant (1-365)"
                />
              </div>
            </CollapsibleSection>

            {/* Patch Severity Configuration */}
            <CollapsibleSection
              title="Patch Severity Configuration"
              sectionKey="patchSeverity"
              icon={<TrendingUp size={20} />}
              isExpanded={expandedSections.patchSeverity}
              onToggle={() => toggleSection('patchSeverity')}
            >
              {/* Info Banner */}
              <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-start gap-3">
                <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-blue-300 font-medium mb-1">Validation Rules:</p>
                  <ul className="text-blue-200 space-y-1 list-disc list-inside">
                    <li>CVE scores: Must be in descending order (Critical highest, Low lowest)</li>
                    <li>Example: Critical: &gt;8, High: 4-8, Medium: 1-4, Low: &lt;1</li>
                    <li>Days ranges: Must be in descending order without gaps (Range 1 highest, Range 4 lowest)</li>
                    <li>Example: Range 1: &gt;90, Range 2: 61-90, Range 3: 31-60, Range 4: &lt;31</li>
                  </ul>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingDown size={18} />
                  CVE Score Mapping (Highest to Lowest)
                </h4>

                <div className="space-y-3">
                  {values.severityCVEMapping?.map((mapping, index) => {
                    const severityColor = getSeverityColor(mapping?.severity);
                    const severityIcon = getSeverityIcon(mapping?.severity);

                    return (
                      <div
                        key={index}
                        className={`bg-[#0f0f0f] p-4 rounded-lg border-2 border-gray-700 hover:border-gray-600 transition-all`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-32">
                            <div className={`px-3 py-2 rounded-lg border-2 ${severityColor} flex items-center gap-2 justify-center`}>
                              {severityIcon}
                              <span className="font-semibold text-sm">{mapping?.severity || 'Unknown'}</span>
                            </div>
                          </div>

                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-400 mb-1">
                              CVE Score Range {index === 0 ? '(Highest)' : index === 3 ? '(Lowest)' : ''}
                            </label>
                            <Field
                              name={`severityCVEMapping.${index}.cveScore`}
                              type="text"
                              placeholder={index === 0 ? 'e.g., >8' : index === 3 ? 'e.g., <1' : 'e.g., 4-8'}
                              className="w-full bg-[#1a1a1a] border border-gray-600 text-white px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all hover:border-gray-500"
                            />
                            <ErrorMessage name={`severityCVEMapping.${index}.cveScore`}>
                              {(msg) => (
                                <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                                  <AlertCircle size={12} />
                                  {msg}
                                </p>
                              )}
                            </ErrorMessage>
                          </div>

                          {index < values.severityCVEMapping.length - 1 && (
                            <div className="flex-shrink-0 text-gray-500">
                              <ChevronDown size={20} />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingDown size={18} />
                  Days Range Configuration (Highest to Lowest)
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {values.daysRange?.map((range, index) => {
                    return (
                      <div key={index} className="relative">
                        <label className="text-xs font-medium text-gray-300 mb-2 flex items-center justify-between">
                          <span>
                            Range {index + 1} {index === 0 ? '(Highest)' : index === 3 ? '(Lowest)' : ''}
                          </span>
                          {index > 0 && <ChevronDown size={14} className="text-gray-500" />}
                        </label>
                        <Field
                          name={`daysRange.${index}`}
                          type="text"
                          placeholder={index === 0 ? '>90' : index === 3 ? '<31' : index === 1 ? '61-90' : '31-60'}
                          className={`w-full bg-[#0f0f0f] border-2 border-gray-600 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all hover:border-gray-500`}
                        />
                        <ErrorMessage name={`daysRange.${index}`}>
                          {(msg) => (
                            <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                              <AlertCircle size={12} />
                              {msg}
                            </p>
                          )}
                        </ErrorMessage>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CollapsibleSection>

            {/* Report Expiry Timing */}
            <CollapsibleSection
              title="Report Expiry Timing"
              sectionKey="reportTiming"
              icon={<AlertCircle size={20} />}
              isExpanded={expandedSections.reportTiming}
              onToggle={() => toggleSection('reportTiming')}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-1 mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Report Listing Threshold (days)</label>
                  <Field
                    type="number"
                    min="1"
                    name="reportListingThresholdInDays"
                    className="w-full bg-[#0f0f0f] border border-gray-600 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all hover:border-gray-500"
                  />
                  <ErrorMessage name="reportListingThresholdInDays">
                    {(msg) => (
                      <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {msg}
                      </p>
                    )}
                  </ErrorMessage>
                </div>
              </div>

              {values.reportExpiryMinutes && Object.keys(values.reportExpiryMinutes).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-1">
                  {Object.entries(values.reportExpiryMinutes).map(([key, rawValue]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-300 mb-2">{formatReportLabel(key)} (minutes)</label>
                      <Field
                        type="number"
                        min="1"
                        name={`reportExpiryMinutes.${key}`}
                        className="w-full bg-[#0f0f0f] border border-gray-600 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all hover:border-gray-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">≈ {formatMinutesReference(rawValue as number)}</p>
                      <ErrorMessage name={`reportExpiryMinutes.${key}`}>
                        {(msg) => (
                          <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                            <AlertCircle size={12} />
                            {msg}
                          </p>
                        )}
                      </ErrorMessage>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-[#0f0f0f] rounded border border-gray-700 text-gray-400 text-center">
                  No report expiry timing available
                </div>
              )}
            </CollapsibleSection>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-6 border-t border-gray-700 sticky bottom-0 bg-[#161617] py-4 -mx-6 px-6">
              <button
                type="submit"
                disabled={saving || isSubmitting}
                className="px-6 py-3 bg-[#b31317] text-white rounded-lg hover:bg-red-700 transition font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                title={saving ? 'Saving...' : ''}
              >
                {saving || isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                {saving || isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={saving || isSubmitting}
                className="px-6 py-3 bg-[#2a2a2a] text-white rounded-lg border border-gray-700 hover:bg-[#333] transition font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                <X size={18} />
                Cancel
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default TenantSettingsEditForm;

const formatReportLabel = (key: string) =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
